import cs from 'classnames';
import { Button, Input, MagellanPopup } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import {
  CSSProperties,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import Cropper, { Area } from 'react-easy-crop';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  PiCheck,
  PiDownload,
  PiFloppyDisk,
  PiFolder,
  PiImageSquare,
  PiImageSquareLight,
  PiMagnifyingGlassMinus,
  PiMagnifyingGlassPlus,
  PiPencil,
  PiPlus,
  PiTrash,
  PiX,
} from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { ILoadingProp } from 'types/components/components.interface';
import {
  IImageSize,
  ISelectOption,
} from 'types/components/inputs/input.interface';
import { getCroppedImg, getImageSize, normalizeImageToJpeg } from 'utils/components/inputs/image';
import './ImageInput.scss';

export interface IImageInputProps extends ILoadingProp {
  fullResolutionUrl?: string;
  previewUrl?: string;
  alt?: string;
  editable?: boolean;
  title?: string;
  onSave?: (
    croppedFile: File,
    actions: PopupActions,
    clearAllValues?: () => void
  ) => void;
  onDelete?: (actions: PopupActions) => void;
  onCancel?: (actions: PopupActions) => void;
  onClose?: (
    event?:
      | SyntheticEvent<Element, Event>
      | KeyboardEvent
      | TouchEvent
      | MouseEvent
      | undefined
  ) => void;
  onChange?: (file: File, actions?: PopupActions) => void;
  disable?: boolean;
  imageSize?: IImageSize;
  imageSizeSelectOptions?: ISelectOption<number>[];
  isError?: boolean;
  imagePlaceholder?: React.ReactNode;
  accept?: string;
  convertToJpeg?: boolean;
  preserveSourceFormat?: boolean;
}

const ImageInput = ({
  alt,
  editable = false,
  title,
  fullResolutionUrl,
  previewUrl,
  loading,
  onDelete,
  onSave,
  onClose,
  onCancel,
  onChange,
  disable,
  imageSize = getImageSize(500),
  imageSizeSelectOptions,
  isError,
  accept = 'image/jpg, image/jpeg',
  convertToJpeg = false,
  preserveSourceFormat = false,
  imagePlaceholder = <PiImageSquareLight className='placeholder' />,
}: IImageInputProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<PopupActions>(null);

  const [newImage, setNewImage] = useState<File | null>(null);
  const [newImageUrl, setNewImageUrl] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<File | null>(null);

  const [newImageSize, setNewImageSize] = useState<IImageSize>(imageSize);

  const [croppedArea, setCroppedArea] = useState<Area>();
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();

  const [imageError, setImageError] = useState(false);

  const imageExists = croppedImage
    ? URL.createObjectURL(croppedImage)
    : imageError
    ? undefined
    : previewUrl ?? fullResolutionUrl;

  const zoomSmoothness = 10;

  const maxZoom = 3 * zoomSmoothness;

  const initialCropValues = { x: 0, y: 0 };

  const [crop, setCrop] = useState(initialCropValues);
  const [zoom, setZoom] = useState(zoomSmoothness);

  const onCropComplete = async (croppedArea: Area, croppedAreaPixels: Area) => {
    if (newImage) {
      const file = await getCroppedImg(
        newImage,
        croppedAreaPixels,
        newImageSize.height,
        newImageSize.width,
        preserveSourceFormat ? 'source' : 'jpeg'
      );

      setCroppedArea(croppedArea);
      setCroppedAreaPixels(croppedAreaPixels);
      setCroppedImage(file);
    }
  };

  const clearCrop = () => {
    setCrop(initialCropValues);
    setZoom(zoomSmoothness);
  };

  const clearNewImage = () => {
    setNewImage(null);
    setNewImageUrl(null);
    setCroppedImage(null);
    clearCrop();
  };

  const cancel = () => {
    popupRef.current && onCancel && onCancel(popupRef.current);
    popupRef.current?.close();
    clearNewImage();
  };

  const openFiles = () => inputRef.current?.click();

  const handleFileChange = async (file: File) => {
    if (convertToJpeg) {
      try {
        const jpegFile = await normalizeImageToJpeg(file);
        setNewImage(jpegFile);
      } catch {
        toast.error(t('toast-message.unsupported-image-format'));
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }

      return;
    }

    setNewImage(file);
  };

  useEffect(() => {
    if (newImage) {
      setNewImageUrl(URL.createObjectURL(newImage));
      setCroppedImage(newImage);
    }
    clearCrop();
  }, [newImage]);

  useEffect(() => {
    croppedImage &&
      onChange &&
      popupRef.current &&
      onChange(croppedImage, popupRef.current);
  }, [croppedImage]);

  useEffect(() => {
    croppedArea &&
      croppedAreaPixels &&
      onCropComplete(croppedArea, croppedAreaPixels);
  }, [newImageSize]);

  useEffect(() => {
    setImageError(false);
  }, [previewUrl, fullResolutionUrl]);

  const triggerClassNames = cs(['image-input-trigger'], {
    editable: !disable && editable,
    loading: loading,
    disable: disable,
    error: !disable && isError,
    downloadable: !disable && !editable && imageExists,
  });

  const onDownloadImage = () =>
    toast.success(t('toast-message.image-downloaded-successfully'));

  const save = () => {
    if (croppedImage && popupRef.current && onSave) {
      onSave(croppedImage, popupRef.current, clearNewImage);
    }
  };

  return (
    <Popup
      ref={popupRef}
      contentStyle={
        {
          '--width-multiple': imageSize.width / imageSize.height,
        } as CSSProperties
      }
      trigger={
        <div className={triggerClassNames}>
          {imageExists ? (
            !editable && fullResolutionUrl ? (
              <MagellanPopups.FilePreviewPopup
                fileUrl={fullResolutionUrl}
                fileName={alt ?? fullResolutionUrl.split('/').pop() ?? 'image'}
                trigger={
                  <div className='download-href'>
                    <img
                      src={imageExists}
                      alt={alt ?? imageExists}
                      loading='lazy'
                      onError={() => setImageError(true)}
                    />
                  </div>
                }
                onDownloadSuccess={onDownloadImage}
              />
            ) : (
              <img
                src={imageExists}
                alt={alt ?? imageExists}
                loading='lazy'
                onError={() => setImageError(true)}
              />
            )
          ) : (
            imagePlaceholder
          )}

          <div className='hover-overlay'>
            {!disable && editable ? (
              imageExists ? (
                <PiPencil />
              ) : (
                <PiPlus className='new' />
              )
            ) : (
              imageExists && <PiDownload />
            )}
          </div>
        </div>
      }
      onOpen={() => !fullResolutionUrl && !newImage && openFiles()}
      disabled={!editable || disable}
      onClose={onClose}
      modal
      nested>
      <MagellanPopup className='image-popup'>
        <MagellanPopup.Header>
          <PiImageSquare />
          {title ?? t('image-input-popup.header')}
        </MagellanPopup.Header>
        {newImage && imageSizeSelectOptions && (
          <div className='size-select'>
            <Input.Select
              label={t('image-input-popup.image-size')}
              value={{
                label: `${newImageSize.height} px`,
                value: newImageSize,
              }}
              options={imageSizeSelectOptions}
              onChange={option =>
                setNewImageSize(getImageSize(option.value as number))
              }
            />
          </div>
        )}
        <div
          className={`crop-wrapper${
            imageSize.width === imageSize.height ? ' square' : ''
          }`}>
          {!newImageUrl && !fullResolutionUrl ? (
            imagePlaceholder
          ) : (
            <Cropper
              image={newImageUrl ?? fullResolutionUrl}
              crop={crop}
              zoom={zoom / zoomSmoothness}
              aspect={imageSize.width / imageSize.height}
              onCropChange={location => newImageUrl && setCrop(location)}
              onCropComplete={onCropComplete}
              onZoomChange={zoom =>
                newImageUrl && setZoom(zoom * zoomSmoothness)
              }
              objectFit='cover'
            />
          )}
        </div>

        <div className='zoom-range'>
          <PiMagnifyingGlassMinus />
          <input
            type='range'
            value={zoom}
            onChange={e => setZoom(parseInt(e.target.value))}
            min={zoomSmoothness}
            max={maxZoom}
            disabled={!newImage || !fullResolutionUrl}
          />
          <PiMagnifyingGlassPlus />
        </div>

        <input
          ref={inputRef}
          type='file'
          accept={accept}
          className='invisible-file-input'
          onChange={e => {
            const file = e.target.files?.[0];

            if (file) {
              void handleFileChange(file);
            }
          }}
        />
        <div className='buttons'>
          <div className='flex gap-2'>
            {onDelete && (
              <Button
                variant={BUTTON_VARIANT.remove}
                onlyIcon
                isClear
                onClick={() =>
                  newImage
                    ? clearNewImage()
                    : popupRef.current && onDelete(popupRef.current)
                }>
                <Button.Description description={t('button.remove')} />
                <Button.Icon icon={<PiTrash />} />
              </Button>
            )}
            <Button onlyIcon isClear onClick={openFiles}>
              <Button.Description description={t('button.selectFile')} />
              <Button.Icon icon={<PiFolder />} />
            </Button>
          </div>
          <div className='flex gap-2'>
            <Button onlyIcon isClear onClick={cancel}>
              <Button.Description description={t('button.cancel')} />
              <Button.Icon icon={<PiX />} />
            </Button>
            {onSave ? (
              <Button
                variant={BUTTON_VARIANT.submit}
                disabled={!newImage}
                onClick={save}>
                <Button.Text text={t('image-input-popup.save')} />
                <Button.Icon icon={<PiFloppyDisk />} />
              </Button>
            ) : (
              <Button
                variant={BUTTON_VARIANT.submit}
                onlyIcon
                disabled={!newImageUrl}
                onClick={() => popupRef.current?.close()}>
                <Button.Description description={t('button.accept')} />
                <Button.Icon icon={<PiCheck />} />
              </Button>
            )}
          </div>
        </div>
      </MagellanPopup>
    </Popup>
  );
};
export default ImageInput;
